import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Users, ArrowRight, Sparkles, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * EventsListing — pulls live published events from Portal and renders them
 * as cards with state-aware CTAs.
 *
 * Props:
 *  - category: optional filter ("camp" | "tournament")
 *  - emptyMessage: text shown when no events
 *  - compact: smaller layout when used as a homepage strip
 */
export default function EventsListing({ category, emptyMessage, compact = false }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const url = category
          ? `${API_URL}/api/events/public?category=${category}`
          : `${API_URL}/api/events/public`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Portal returned ${res.status}`);
        const data = await res.json();
        setEvents(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('[EventsListing] Failed to fetch events', e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [category]);

  if (loading) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm" data-testid="events-loading">
        Loading events...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-destructive text-sm flex items-center justify-center gap-2" data-testid="events-error">
        <AlertCircle className="h-4 w-4" />
        Could not load events. Please try again later.
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <Card className="bg-gradient-card border-border" data-testid="events-empty">
        <CardContent className="text-center py-12">
          <Calendar className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <p className="text-muted-foreground text-sm">
            {emptyMessage || 'No upcoming events. Check back soon!'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`grid gap-6 ${compact ? 'sm:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2'}`} data-testid="events-grid">
      {events.map((event) => (
        <EventCard key={event.id} event={event} onRegister={(slug) => navigate(`/event/${slug}`)} />
      ))}
    </div>
  );
}

function getEventState(event) {
  const today = new Date().toISOString().slice(0, 10);
  const open = event.registration_open_date || null;
  const close = event.registration_close_date || null;
  const remaining = event.spots_remaining;

  if (open && today < open) return 'coming_soon';
  if (close && today > close) return 'closed';
  if (typeof remaining === 'number' && remaining <= 0) return 'sold_out';
  if (typeof remaining === 'number' && remaining > 0 && remaining <= 5) return 'almost_full';
  return 'open';
}

function formatDateRange(start, end) {
  if (!start) return '';
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  const s = new Date(start).toLocaleDateString('en-SG', opts);
  if (!end || end === start) return s;
  const e = new Date(end).toLocaleDateString('en-SG', opts);
  return `${s} – ${e}`;
}

function EventCard({ event, onRegister }) {
  const state = getEventState(event);
  const isCamp = event.category === 'camp';
  const accent = isCamp ? 'yellow' : 'blue';

  const stateConfig = {
    coming_soon: { label: 'Coming Soon', color: 'bg-blue-500/20 text-blue-300 border-blue-500/40', disabled: true, btn: 'Registration Opens Soon' },
    open: { label: null, color: '', disabled: false, btn: 'Register Now' },
    almost_full: { label: `Only ${event.spots_remaining} spots left!`, color: 'bg-orange-500/20 text-orange-300 border-orange-500/40', disabled: false, btn: 'Register Now' },
    sold_out: { label: 'Sold Out', color: 'bg-red-500/20 text-red-300 border-red-500/40', disabled: true, btn: 'Join Waitlist' },
    closed: { label: 'Registration Closed', color: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/40', disabled: true, btn: 'Closed' },
  };
  const ui = stateConfig[state];

  const handleClick = () => {
    if (state === 'sold_out') {
      window.location.href = `mailto:academy@sccfootball.com?subject=Waitlist Request: ${event.name}&body=Hi, I'd like to be added to the waitlist for ${event.name}.`;
      return;
    }
    if (ui.disabled) return;
    onRegister(event.slug);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <Card
        className={`bg-gradient-to-br ${isCamp ? 'from-yellow-900/30 to-amber-900/20 border-yellow-600/50' : 'from-blue-900/30 to-indigo-900/20 border-blue-600/50'} h-full hover:shadow-xl transition-all relative overflow-hidden`}
        data-testid={`event-card-${event.slug}`}
      >
        {!ui.disabled && (
          <div className="absolute top-4 right-4">
            <Sparkles className={`h-5 w-5 ${isCamp ? 'text-yellow-400' : 'text-blue-400'} animate-pulse`} />
          </div>
        )}

        <CardHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`${isCamp ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40' : 'bg-blue-500/20 text-blue-300 border-blue-500/40'}`}>
              <Calendar className="h-3 w-3 mr-1" />
              {isCamp ? 'Camp' : 'Tournament'}
            </Badge>
            {ui.label && (
              <Badge className={ui.color} data-testid={`event-state-${event.slug}`}>
                {ui.label}
              </Badge>
            )}
          </div>
          <CardTitle className={`font-display text-2xl mt-3 ${isCamp ? 'text-yellow-300' : 'text-blue-300'}`}>
            {event.name}
          </CardTitle>
          {event.description && (
            <CardDescription className="text-muted-foreground line-clamp-2">
              {event.description}
            </CardDescription>
          )}
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-card-foreground">
              <Calendar className={`h-4 w-4 ${isCamp ? 'text-yellow-400' : 'text-blue-400'}`} />
              {formatDateRange(event.start_date, event.end_date)}
            </div>
            {event.location && (
              <div className="flex items-center gap-2 text-card-foreground">
                <MapPin className={`h-4 w-4 ${isCamp ? 'text-yellow-400' : 'text-blue-400'}`} />
                {event.location}
              </div>
            )}
            {event.age_groups && event.age_groups.length > 0 && (
              <div className="flex items-center gap-2 text-card-foreground">
                <Users className={`h-4 w-4 ${isCamp ? 'text-yellow-400' : 'text-blue-400'}`} />
                {event.age_groups.join(', ')}
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-white/10 flex items-center justify-between">
            <div>
              <div className="text-xs text-muted-foreground">From</div>
              <div className={`text-2xl font-bold ${isCamp ? 'text-yellow-300' : 'text-blue-300'}`}>
                ${event.price} <span className="text-xs font-normal text-muted-foreground">{event.currency}</span>
              </div>
            </div>
            <Button
              onClick={handleClick}
              disabled={ui.disabled && state !== 'sold_out'}
              className={`${isCamp ? 'bg-yellow-500 text-black hover:bg-yellow-400' : 'bg-blue-500 text-white hover:bg-blue-400'} font-bold disabled:opacity-60 disabled:cursor-not-allowed`}
              data-testid={`event-register-btn-${event.slug}`}
            >
              {ui.btn}
              {!ui.disabled && <ArrowRight className="h-4 w-4 ml-2" />}
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
